import { useState } from "react";
import axios from "axios";
import { motion, AnimatePresence } from "framer-motion";
import { ArrowRight, CheckCircle2, Loader2 } from "lucide-react";
import { analytics } from "@heycatch/sdk";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

export const WaitlistForm = ({ source = "homepage", dark = false, testId = "waitlist" }) => {
  const [email, setEmail] = useState("");
  const [state, setState] = useState("idle");
  const [error, setError] = useState("");

  const submit = async (e) => {
    e.preventDefault();
    if (state === "loading" || state === "success") return;
    setState("loading");
    setError("");
    try {
      await axios.post(`${API}/waitlist`, { email, source });
      const normalized = email.trim().toLowerCase();
      analytics.setIdentity(normalized, { email: normalized }, { signup_source: source });
      analytics.trackEvent("signup_completed", { source, plan: "waitlist" });
      setState("success");
    } catch (err) {
      setState("idle");
      setError(
        err?.response?.status === 422
          ? "Please enter a valid email address."
          : "Something went wrong — please try again."
      );
    }
  };

  return (
    <div data-testid={`${testId}-form`} className="w-full max-w-md">
      <AnimatePresence mode="wait">
        {state === "success" ? (
          <motion.p
            key="success"
            data-testid={`${testId}-success`}
            initial={{ opacity: 0, y: 12 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.5, ease: [0.22, 1, 0.36, 1] }}
            className={`flex items-center gap-3 text-base font-medium ${dark ? "text-white" : "text-neutral-900"}`}
          >
            <CheckCircle2 className="h-5 w-5 shrink-0 text-teal" aria-hidden="true" />
            You're on the list — NXTPlate news and updates, straight to your inbox.
          </motion.p>
        ) : (
          <motion.form
            key="form"
            onSubmit={submit}
            exit={{ opacity: 0, y: -8 }}
            className={`flex items-center gap-2 rounded-full border p-1.5 transition-shadow duration-300 focus-within:shadow-[0_8px_24px_rgba(0,0,0,0.08)] ${
              dark ? "border-white/20 bg-white/10" : "border-neutral-200 bg-white"
            }`}
          >
            <input
              type="email"
              required
              value={email}
              onChange={(e) => setEmail(e.target.value)}
              placeholder="you@example.com"
              data-testid={`${testId}-email-input`}
              aria-label="Email address for launch notification"
              className={`w-full bg-transparent px-4 py-2.5 text-sm outline-none ${
                dark ? "text-white placeholder:text-neutral-500" : "text-neutral-900 placeholder:text-neutral-400"
              }`}
            />
            <button
              type="submit"
              data-testid={`${testId}-submit-button`}
              disabled={state === "loading"}
              className="inline-flex shrink-0 items-center gap-2 rounded-full bg-coral px-5 py-2.5 text-sm font-semibold text-white transition-all duration-200 hover:bg-coral-dark hover:shadow-[0_8px_20px_rgba(255,107,87,0.35)] disabled:opacity-70"
            >
              {state === "loading" ? (
                <Loader2 className="h-4 w-4 animate-spin" aria-hidden="true" />
              ) : (
                <>
                  Notify me
                  <ArrowRight className="h-4 w-4" aria-hidden="true" />
                </>
              )}
            </button>
          </motion.form>
        )}
      </AnimatePresence>
      {error && (
        <p data-testid={`${testId}-error`} className="mt-2 px-2 text-sm text-coral-dark">
          {error}
        </p>
      )}
    </div>
  );
};
